import {type FC, Suspense} from 'react';
import {TabsContent, TabsList, TabsTrigger} from '@radix-ui/react-tabs';
import clsx from 'clsx';
import slugify from 'slugify';

import type {ContainerSpacing} from '@/modules/foundation/components/container';
import {Container} from '@/modules/foundation/components/container';
import type {ImageWeb} from '@/modules/foundation/components/image/types';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import type {ProductInCard} from '../../shared/types';
import {ProductCard, SpecialProductCard} from '../product-card';
import {TabsRootProductList} from '../tabs/Tabs';

type ProductType = {
	_id: string;
	name: string;
	products: KeyedArray<ProductInCard>;
};

type Category = {
	_id: string;
	name: string;
	productTypes: ProductType[];
	special?: boolean;
};

export type ProductList = Category[];

export type SpecialProduct = {
	_key: string;
	image?: ImageWeb;
	href: string;
	title: string;
};

interface Props {
	title?: string;
	tagline?: string;
	spacingTop?: ContainerSpacing;
	spacingBottom?: ContainerSpacing;
	products: ProductList;
	specialProducts?: SpecialProduct[];
}

function slugifyCategory(category: string) {
	return slugify(category, {lower: true});
}

const ProductList: FC<Props> = ({
	title,
	tagline,
	spacingBottom,
	spacingTop,
	products,
	specialProducts,
}) => {
	const hasSpecialProducts = Boolean(specialProducts && specialProducts.length > 0);
	// The Spesialprodukter tab normally arrives rank-ordered in `products` (a
	// `special` Category entry). Pinning it last is the fallback for data where
	// the singleton has no Kategori yet.
	const pinnedSpecialTab = hasSpecialProducts && !products.some((category) => category.special);

	const specialProductsGrid = (
		<ul className={clsx('grid', 'grid-cols-4', 'gap-8')}>
			{specialProducts?.map((product) => (
				<li key={product._key}>
					<SpecialProductCard product={product} />
				</li>
			))}
		</ul>
	);

	return (
		<Container spacingBottom={spacingBottom} spacingTop={spacingTop}>
			{title && (
				<Heading level={2} spacing>
					{title}
				</Heading>
			)}
			{tagline && <Paragraph>{tagline}</Paragraph>}

			<Suspense>
				<TabsRootProductList
					initialCategoryName={
						products[0] ? slugifyCategory(products[0].name) : 'spesialprodukter'
					}
				>
					<TabsList className={clsx('flex', 'flex-wrap', 'gap-2', 'mb-8')}>
						{products.map((category) => (
							<TabsTrigger
								key={category._id}
								value={slugifyCategory(category.name)}
								className={clsx(
									'aria-selected:bg-dypgronn-industri',
									'aria-selected:text-white',
									'bg-white',
									'text-dypgronn-industri',
									'px-6',
									'py-4',
									'shadow-lg',
									'rounded-sm',
									'uppercase',
									'text-xs',
									'font-bold',
									'flex-1',
									'text-center',
								)}
							>
								{category.name}
							</TabsTrigger>
						))}
						{pinnedSpecialTab ? (
							<TabsTrigger
								value="spesialprodukter"
								className={clsx(
									'aria-selected:bg-dypgronn-industri',
									'aria-selected:text-white',
									'bg-white',
									'text-dypgronn-industri',
									'px-6',
									'py-4',
									'shadow-lg',
									'rounded-sm',
									'uppercase',
									'text-xs',
									'font-bold',
									'flex-1',
									'text-center',
								)}
							>
								Spesialprodukter
							</TabsTrigger>
						) : null}
					</TabsList>
					{products.map((category) => (
						<TabsContent key={category._id} value={slugifyCategory(category.name)}>
							{category.special ? (
								specialProductsGrid
							) : (
								<div className={clsx('grid', 'gap-12')}>
									{category.productTypes.map((productType) => (
										<div key={productType._id}>
											<h2
												className={clsx(
													'text-gray-700',
													'text-2xl',
													'font-medium',
													'mb-2',
												)}
											>
												{productType.name}
											</h2>
											<ul
												className={clsx(
													'grid',
													'sm:grid-cols-2',
													'md:grid-cols-3',
													'lg:grid-cols-4',
													'gap-8',
												)}
											>
												{productType.products.map((prod) => (
													<li key={prod._id}>
														<ProductCard product={prod} />
													</li>
												))}
											</ul>
										</div>
									))}
								</div>
							)}
						</TabsContent>
					))}
					{pinnedSpecialTab ? (
						<TabsContent value="spesialprodukter">{specialProductsGrid}</TabsContent>
					) : null}
				</TabsRootProductList>
			</Suspense>
		</Container>
	);
};
export default ProductList;
